import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { withStyles, Button, CircularProgress, Dialog, Paper } from '@material-ui/core';
import PresetWorkVerifierSettings from './PresetWorkVerifierSettings';
import { snackActions } from '../../../sc-utilities/SnackBar';
import { CancelToken } from 'axios';
import * as actions from '../../../sc-customer/Customer/store/customerActions';
import { securazeInitialValues } from '../index';
import AppLogoLoading from '../../../sc-logo';

const styles = (theme) => ({
    root: {
        height: 'auto',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    paper: {
        backgroundColor: 'transparent',
        boxShadow: 'none',
    },
    paperForm: {
        width: 'auto',
        height: 'auto',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        paddingTop: theme.spacing(2),
        marginLeft: theme.spacing(0),
        marginRight: theme.spacing(0),
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(4),
        paddingBottom: theme.spacing(1),
    },
    submit: {
        width: '100%',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default,
        },
    },
});

const WorkVerifierPresets = (props) => {
    const {
        classes,
        activeCustomerId,
        translations,
        updatePresets,
        loadPresets,
        idParams,
        customer,
        onLoadCustomer,
        customerUpdatePresets,
    } = props;

    const [loading, setLoading] = useState(true);

    let presets = securazeInitialValues.WorkVerifier;
    if (customer?.Presets) {
        const customerPresets = JSON.parse(customer.Presets);
        if (customerPresets?.WorkVerifier) {
            presets = customerPresets.WorkVerifier;
        }
        presets = {
            ...presets,
            PresetsEnabled: customerPresets?.Normal?.PresetsEnabled,
        };
    }

    const initialValues = {
        PresetsEnabled: presets?.PresetsEnabled ?? false,
        AutoShutdown: presets?.AutoShutdown ?? false,
        AutoStartVerification: presets?.AutoStartVerification ?? false,
        VerificationPercentDone: presets?.VerificationPercentDone ?? 100,
        NormalScreenBrightness: presets?.NormalScreenBrightness ?? 100,
        ReducedScreenBrightness: presets?.ReducedScreenBrightness ?? 80,
    };

    const customerCancel = CancelToken?.source();

    const collectPresets = (data) => {
        const presetData = {
            WorkVerifier: {
                AutoShutdown: !!data?.AutoShutdown,
                AutoStartVerification: !!data?.AutoStartVerification,
                VerificationPercentDone: data?.VerificationPercentDone ?? 100,
                NormalScreenBrightness: data?.NormalScreenBrightness ?? 100,
                ReducedScreenBrightness: data?.ReducedScreenBrightness ?? 80,
            },
        };
        return presetData;
    };

    const handleLoadPresets = () => {
        loadPresets
            ? loadPresets()
            : onLoadCustomer(
                  { CustomerID: activeCustomerId, ActiveCustomerID: activeCustomerId },
                  { cancelToken: customerCancel }
              );
    };

    const handleUpdatePresets = async (presets) => {
        return (await updatePresets)
            ? updatePresets({ Presets: presets, ...idParams, ActiveCustomerID: activeCustomerId })
            : customerUpdatePresets({
                  Presets: presets,
                  CustomerID: customer?.CustomerID,
                  ActiveCustomerID: activeCustomerId,
              });
    };

    useEffect(() => {
        handleLoadPresets();

        setLoading(false);

        return () => {
            customerCancel.cancel('Operation canceled by user');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCustomerId]);

    let formSettings = {
        ButtonSubmit: {
            Title: translations['save'] || 'SAVE',
            Color: 'primary',
        },
    };

    const handleSubmit = async (data) => {
        const presets = collectPresets(data);

        const serverResult = await handleUpdatePresets(presets);
        handleLoadPresets();

        setLoading(false);
        if (serverResult.status) {
            return snackActions.snackBarComponent(translations['update_presets'] || 'Presets updated.', 'success');
        } else {
            return snackActions.snackBarComponent(
                translations['failed_to_update_presets'] || 'Error: Failed to update presets',
                'error'
            );
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
                open={loading}
                PaperProps={{
                    classes: {
                        root: classes.paper,
                    },
                }}
            >
                <AppLogoLoading />
            </Dialog>
            <Paper className={classes.paperForm}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values, { setSubmitting }) => {
                        setSubmitting(true);
                        await handleSubmit(values);
                        setSubmitting(false);
                    }}
                    enableReinitialize
                    validationSchema={Yup.object().shape(formSettings.Validation)}
                >
                    {(formikProps) => {
                        const { values, isSubmitting, setFieldValue, handleChange, isValid, setValues, handleSubmit } =
                            formikProps;

                        const workVerifierProps = {
                            translations: translations,
                            handleChange: handleChange,
                            values: values,
                            setFieldValue: setFieldValue,
                            setValues: setValues,
                        };

                        return (
                            <form
                                className={classes.form}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit(e);
                                }}
                            >
                                <PresetWorkVerifierSettings {...workVerifierProps} />

                                <div>
                                    {formSettings.ButtonSubmit ? (
                                        <Button
                                            className={classes.submit}
                                            variant="contained"
                                            color={formSettings.ButtonSubmit.Color}
                                            type="submit"
                                            disabled={!isValid || isSubmitting || loading}
                                        >
                                            {isSubmitting ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                formSettings.ButtonSubmit.Title
                                            )}
                                        </Button>
                                    ) : null}
                                </div>
                            </form>
                        );
                    }}
                </Formik>
            </Paper>
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        activeCustomerId: state?.core?.login?.activeCustomer?.CustomerID || null,
        customer: state?.customer?.customers?.loaded || null,
        permissions: state?.core?.login?.permissions || [],
        translations: state?.translate?.translations?.activeTranslations || {},
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onLoadCustomer: (customerID, options) => dispatch(actions.loadCustomer(customerID, options)),
        customerUpdatePresets: (params, options) => dispatch(actions.updatePresets(params, options)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(WorkVerifierPresets));
